import axios from "axios";
import { getCookie } from "../../../helper/cookieHelper";
import { ACCESS_TOKEN } from "../../constants";
import { createRequest, validateAndParseResponse } from "../../utils/hmac-utils";
import {
  LoyaltyEnrollRequest,
  LoyaltyApplyRewardRequest,
  LoyaltyPointsPreviewRequest,
  LoyaltyOrderIdentifyRequest,
  LoyaltyOrderCompleteRequest,
  LoyaltyAccruePointsRequest,
} from "./loyaltyModels";
import API from "../../Api";

// ╔════════════════════════════════════════════════════════════════╗
// ║  Loyalty APIs run on the NestJS backend with HMAC protection. ║
// ║  - X-source: CA activates HmacMiddleware on the backend.      ║
// ║  - POST/DELETE bodies are wrapped in a signed HMAC envelope.  ║
// ║  - GET requests carry no body; session key is derived from    ║
// ║    the Bearer token for response signing only.                ║
// ║  - Responses are HMAC-signed; interceptor validates + unwraps ║
// ║    transparently so sagas receive the plain controller data.  ║
// ╚════════════════════════════════════════════════════════════════╝

const LOYALTY_API = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT_LOYALTY,
  timeout: 60000,
});

LOYALTY_API.interceptors.request.use((config) => {
  if (!(config as any).skipXSource) {
    config.headers["X-source"] = "CA";
  }
  if ((config as any).skipAuth) return config;
  const token = getCookie(ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Validate and unwrap the HMAC-signed response envelope.
// The backend wraps every successful response as:
//   { data, dataString, timestamp, nonce, signature }
// After validation, response.data is replaced with the plain controller data
// so saga code (response.data?.data) continues to work unchanged.
LOYALTY_API.interceptors.response.use(async (response) => {
  const envelope = response.data;
  if (
    envelope &&
    typeof envelope === "object" &&
    "dataString" in envelope &&
    "signature" in envelope
  ) {
    const result = await validateAndParseResponse(envelope);
    response.data = result.data;
  }
  return response;
});

function loyaltyHeaders(brandId: string) {
  return { headers: { "x-brand-id": brandId } };
}

// ── GET endpoints — no body, HMAC session key derived from Bearer token ─────

// API 78: Get Customer Loyalty Dashboard
export function loyaltyDashboardAPI(loyaltyCustomerId: string, locationId: string, brandId: string) {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/customer/${loyaltyCustomerId}/dashboard?location_id=${locationId}`,
    ...loyaltyHeaders(brandId),
    skipXSource: true,
  } as any);
}

// API 74: Get Available Rewards for Customer
export function loyaltyAvailableRewardsAPI(
  loyaltyCustomerId: string,
  locationId: string,
  brandId: string,
  channel: string = "online",
  orderTotal?: number,
  orderId?: string,
) {
  let url = `/api/v1/loyalty/redemptions/eligible?loyalty_customer_id=${loyaltyCustomerId}&location_id=${locationId}&channel=${channel}`;
  if (orderTotal !== undefined) url += `&order_total=${orderTotal}`;
  if (orderId) url += `&order_id=${encodeURIComponent(orderId)}`;
  return LOYALTY_API({
    method: "get",
    url,
    ...loyaltyHeaders(brandId),
    skipXSource: true,
  } as any);
}

// API 79: Get Checkout Summary
export function loyaltyCheckoutSummaryAPI(orderId: string, locationId: string, brandId: string) {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/orders/${orderId}/checkout-summary?location_id=${locationId}`,
    ...loyaltyHeaders(brandId),
  });
}

// API 74: Get Point Receipt (immutable, idempotent receipt for a completed order)
export function loyaltyReceiptDataAPI(orderId: string, brandId: string) {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/orders/${encodeURIComponent(orderId)}/point-receipt`,
    ...loyaltyHeaders(brandId),
  });
}

// API 86: Get Wallet Pass Info
export function loyaltyGetWalletPassAPI(loyaltyCustomerId: string, brandId: string) {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/wallet/passes/${loyaltyCustomerId}`,
    ...loyaltyHeaders(brandId),
  });
}

// API 10: Get Full Transaction History for a loyalty member
export function loyaltyCustomerTransactionsAPI(loyaltyCustomerId: string, brandId: string) {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/customer/${encodeURIComponent(loyaltyCustomerId)}/transactions`,
    ...loyaltyHeaders(brandId),
  });
}

// ── POST/DELETE endpoints — body wrapped in HMAC signed envelope ─────────────

// API 65: Lookup Loyalty Customer by Phone
export function loyaltyLookupAPI(phone: string, locationId: string, brandId: string) {
  return LOYALTY_API.post(`/api/v1/loyalty/lookup/phone`, { phone, location_id: locationId }, {
    headers: { "x-brand-id": brandId },
    skipAuth: true,
    skipXSource: true,
  } as any);
}

// API 68: Enroll New Loyalty Customer
export async function loyaltyEnrollAPI(details: LoyaltyEnrollRequest, brandId: string) {
  const body = await createRequest(details);
  return LOYALTY_API({
    method: "post",
    url: "/api/v1/loyalty/enroll",
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 68: Redeem Reward (deducts points, creates redemption)
export async function loyaltyApplyRewardAPI(
  orderId: string,
  details: LoyaltyApplyRewardRequest,
  brandId: string,
) {
  const payload = {
    loyalty_customer_id: details.loyalty_customer_id,
    ...(details.reward_ids ? { reward_ids: details.reward_ids } : { reward_id: details.reward_id }),
    location_id: details.location_id,
    channel: details.channel || "online",
    ...(orderId ? { order_id: orderId } : {}),
  };
  const body = await createRequest(payload);
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/redemptions/redeem`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 70: Void Redemption (refunds points back to customer)
export async function loyaltyRemoveRewardAPI(
  _orderId: string,
  applicationId: string,
  brandId: string,
) {
  const body = await createRequest({ reason: "Customer removed reward from cart" });
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/redemptions/${applicationId}/void`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 63: Calculate Points Preview
export async function loyaltyPointsPreviewAPI(
  _orderId: string,
  details: LoyaltyPointsPreviewRequest,
  brandId: string,
) {
  const body = await createRequest(details);
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/points/preview`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 62: Accrue Points — fired after successful payment
export async function loyaltyAccruePointsAPI(
  details: LoyaltyAccruePointsRequest,
  brandId: string,
) {
  const body = await createRequest(details);
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/points/accrue`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 67: Identify / Sign-In Loyalty Customer on Order
export async function loyaltyOrderIdentifyAPI(
  details: LoyaltyOrderIdentifyRequest,
  brandId: string,
) {
  const body = await createRequest(details);
  return LOYALTY_API({
    method: "post",
    url: "/api/v1/loyalty/orders/link",
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 70: Remove Loyalty Customer from Order
// DELETE is in BODY_METHODS on the backend — must send HMAC envelope even with empty payload.
export async function loyaltyOrderRemoveIdentifyAPI(orderId: string, brandId: string) {
  const body = await createRequest({});
  return LOYALTY_API({
    method: "delete",
    url: `/api/v1/loyalty/orders/${orderId}/identify`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 77: Finalize Order & Complete
export async function loyaltyOrderCompleteAPI(
  orderId: string,
  details: LoyaltyOrderCompleteRequest,
  brandId: string,
) {
  const body = await createRequest(details);
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/orders/${orderId}/complete`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// API 85: Generate Apple/Google Wallet Pass
export async function loyaltyGenerateWalletPassAPI(
  loyaltyCustomerId: string,
  passType: "apple" | "google",
  programId: string,
  brandId: string,
) {
  const body = await createRequest({
    loyalty_customer_id: loyaltyCustomerId,
    pass_type: passType,
    program_id: programId,
  });
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/wallet/passes`,
    data: body,
    ...loyaltyHeaders(brandId),
  });
}

// CD flow: v2 orders redeem endpoint — used for remove (reward_ids: []) in CD flow
export function loyaltyV2OrdersRedeemAPI(payload: {
  loyalty_customer_id: string;
  reward_ids: string[];
  location_id: string;
  order_id: string;
  channel: string;
  points_to_redeem: number;
}) {
  return API.post("/api/v2/orders/redeem", payload);
}
